import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

import { DAY_OF_MONTH_OPTIONS, DAY_OF_WEEK_OPTIONS, MONTH_OPTIONS } from '../../../../../../../constants/calendar-anchor.constants';
import { FREQUENCY_OPTIONS, AutomaticFrequency } from '../../../../../../../types/measure.interface';
import { CesHourpickerFieldComponent } from '../../../../../../ui/ces-hourpicker-field/ces-hourpicker-field.component';
import { CesSelectFieldComponent } from '../../../../../../ui/ces-select-field/ces-select-field.component';
import { CesTextFieldComponent } from '../../../../../../ui/ces-text-field/ces-text-field.component';
import { BaseMeasureInfoComponent } from '../../shared/base-measure-info.component';

import { CesTooltipAutoDismissDirective } from '../../../../../../../directives/ces-tooltip-auto-dismiss.directive';
const WEEKLY = AutomaticFrequency.OneWeek;
const MONTHLY = AutomaticFrequency.OneMonth;
const LONG = new Set<AutomaticFrequency>([AutomaticFrequency.FourMonths, AutomaticFrequency.SixMonths, AutomaticFrequency.OneYear]);

@Component({
  selector: 'app-automatic-measure-info',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    ReactiveFormsModule,
    CesTextFieldComponent,
    CesSelectFieldComponent,
    CesHourpickerFieldComponent,
    ToggleSwitchModule,
    TooltipModule,
    TranslocoModule,
  ],
  templateUrl: './automatic-measure-info.component.html',
})
export class AutomaticMeasureInfoComponent extends BaseMeasureInfoComponent implements OnInit {
  protected readonly FREQUENCY_OPTIONS = FREQUENCY_OPTIONS;
  protected readonly DAY_OF_WEEK_OPTIONS = DAY_OF_WEEK_OPTIONS;
  protected readonly DAY_OF_MONTH_OPTIONS = DAY_OF_MONTH_OPTIONS;
  protected readonly MONTH_OPTIONS = MONTH_OPTIONS;

  private readonly freqDestroyRef = inject(DestroyRef);
  private readonly freq = signal<AutomaticFrequency | null>(null);

  protected readonly showDayOfWeek = computed(() => this.freq() === WEEKLY);
  protected readonly showDayOfMonth = computed(() => {
    const f = this.freq();
    return f === MONTHLY || (f != null && LONG.has(f));
  });
  protected readonly showMonth = computed(() => {
    const f = this.freq();
    return f != null && LONG.has(f);
  });

  ngOnInit(): void {
    const ctrl = this.parentForm().get('frequency');
    if (!ctrl) {
      return;
    }
    this.freq.set(ctrl.value as AutomaticFrequency | null);
    ctrl.valueChanges.pipe(takeUntilDestroyed(this.freqDestroyRef)).subscribe(v => this.freq.set(v as AutomaticFrequency | null));
  }
}
